// frontend/src/Components/Tasks/TaskCommentList.jsx
//
// Renders the flat list of comments + nested replies for a task. The backend
// stores comments and replies in the same collection (`taskComments`) keyed
// on `parentCommentId` — null for top-level, otherwise the parent's id. We
// group on the client and render replies indented under their parent.

import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import MentionTextarea from './MentionTextarea';

function formatTime(when) {
  if (!when) return '';
  try {
    return new Date(when).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

// Render mentions inside the comment text as styled chips. We don't trust
// any HTML — the backend already escaped the input. Mentions appear as
// `@FirstName Lastname` substrings; we look them up by display name in the
// supplied `practiceUsers` map and highlight matches.
function renderCommentText(text, practiceUsers) {
  const names = (practiceUsers || []).map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim()).filter(Boolean);
  if (names.length === 0) return text;
  // Sort longest first to avoid partial-match collisions ("Anna" before "Anna Smith").
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})`, 'g');
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    parts.push(
      <span key={`m-${m.index}`} className="text-primary fw-semibold">@{m[1]}</span>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function CommentRow({ comment, replies = [], practiceUsers, onReply, replying, replyingId, replyDraft, setReplyDraft, onSubmitReply, submitting, isLast, disableReplies = false }) {
  const author = comment.createdByName || (comment.isSystemComment ? 'System' : 'User');
  const time = formatTime(comment.createdAt);
  const isReplying = replyingId === comment._id;

  // Soft separator between top-level comments — visually groups each comment
  // (with its replies) as a single unit. The last row drops the divider so
  // it doesn't double-up against the "Add a comment" composer below.
  return (
    <div
      className="task-comment pb-3 mb-3"
      style={isLast ? {} : { borderBottom: '1px solid #e9ecef' }}
    >
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 mb-1 small">
            <strong>{author}</strong>
            {comment.isSystemComment && <Badge bg="secondary" pill>System</Badge>}
            {comment.isExternalJira && (
              <Badge bg="info" pill title="Reply from Practo Support via JIRA">
                <i className="fa-light fa-life-ring me-1" />JIRA
              </Badge>
            )}
            <span className="text-muted">{time}</span>
          </div>
          <div className="small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {renderCommentText(comment.commentText, practiceUsers)}
          </div>
          {!comment.isSystemComment && !disableReplies && (
            <div className="mt-1">
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={() => onReply?.(comment._id)}
                disabled={submitting}
              >
                <i className="fa-light fa-reply me-1" />Reply
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Replies indented under the parent. */}
      {replies.length > 0 && (
        <div className="ps-4 mt-2 border-start border-2">
          {replies.map((r) => (
            <div key={r._id} className="task-comment mb-2">
              <div className="d-flex align-items-center gap-2 mb-1 small">
                <strong>{r.createdByName || 'User'}</strong>
                <span className="text-muted">{formatTime(r.createdAt)}</span>
              </div>
              <div className="small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {renderCommentText(r.commentText, practiceUsers)}
              </div>
            </div>
          ))}
        </div>
      )}

      {isReplying && (
        <div className="ps-4 mt-2">
          <MentionTextarea
            value={replyDraft.text}
            onChange={(payload) => setReplyDraft(payload)}
            practiceUsers={practiceUsers}
            placeholder={`Reply to ${author}…`}
            rows={2}
            disabled={submitting}
            resetSignal={replyDraft.resetSignal || 0}
          />
          <div className="mt-1 d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={submitting || !replyDraft.text?.trim()}
              onClick={() => onSubmitReply?.(comment._id)}
            >
              {submitting ? 'Posting…' : 'Post reply'}
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={() => onReply?.(null)}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaskCommentList({
  comments = [],
  practiceUsers = [],
  onAddComment,
  onAddReply,
  submitting = false,
  // SUPPORT tasks hide replies because nested replies don't round-trip to JIRA
  // — only top-level comments push back. Forcing a flat thread keeps the JIRA
  // and PractoSync sides consistent.
  disableReplies = false,
}) {
  const [draft, setDraft] = useState({ text: '', mentionedUserIds: [], resetSignal: 0 });
  const [replyDraft, setReplyDraft] = useState({ text: '', mentionedUserIds: [], resetSignal: 0 });
  const [replyingId, setReplyingId] = useState(null);

  const grouped = useMemo(() => {
    const top = comments.filter((c) => !c.parentCommentId);
    const replies = {};
    for (const c of comments) {
      if (c.parentCommentId) {
        const key = String(c.parentCommentId);
        replies[key] = replies[key] || [];
        replies[key].push(c);
      }
    }
    return { top, replies };
  }, [comments]);

  const handleSubmit = async () => {
    if (!draft.text?.trim()) return;
    await onAddComment?.({ commentText: draft.text, mentionedUserIds: draft.mentionedUserIds });
    setDraft((d) => ({ text: '', mentionedUserIds: [], resetSignal: (d.resetSignal || 0) + 1 }));
  };
  const handleSubmitReply = async (parentId) => {
    if (!replyDraft.text?.trim()) return;
    await onAddReply?.({
      parentCommentId: parentId,
      commentText: replyDraft.text,
      mentionedUserIds: replyDraft.mentionedUserIds,
    });
    setReplyDraft((d) => ({ text: '', mentionedUserIds: [], resetSignal: (d.resetSignal || 0) + 1 }));
    setReplyingId(null);
  };

  return (
    <div>
      {grouped.top.length === 0 && (
        <div className="text-muted small mb-3">No comments yet.</div>
      )}
      {grouped.top.map((c, idx) => (
        <CommentRow
          key={c._id}
          comment={c}
          replies={grouped.replies[String(c._id)] || []}
          practiceUsers={practiceUsers}
          onReply={(id) => {
            setReplyingId(id);
            setReplyDraft((d) => ({ text: '', mentionedUserIds: [], resetSignal: (d.resetSignal || 0) + 1 }));
          }}
          replyingId={replyingId}
          replyDraft={replyDraft}
          setReplyDraft={setReplyDraft}
          onSubmitReply={handleSubmitReply}
          submitting={submitting}
          isLast={idx === grouped.top.length - 1}
          disableReplies={disableReplies}
        />
      ))}

      <div className="mt-3">
        <Form.Label className="small text-muted mb-1">Add a comment</Form.Label>
        <MentionTextarea
          value={draft.text}
          onChange={(payload) => setDraft((d) => ({ ...payload, resetSignal: d.resetSignal }))}
          practiceUsers={practiceUsers}
          placeholder="Add a comment… use @ to tag someone"
          rows={2}
          disabled={submitting}
          resetSignal={draft.resetSignal || 0}
        />
        <div className="mt-2 d-flex justify-content-end">
          <Button
            variant="primary"
            size="sm"
            disabled={submitting || !draft.text?.trim()}
            onClick={handleSubmit}
          >
            {submitting ? 'Posting…' : 'Post comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
